import React, { createContext, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useProSignup } from "@/components/ProfessionalSignupModal";
import ForgotPasswordPanel from "@/components/auth/ForgotPasswordPanel";

const AuthModalCtx = createContext({ open: () => {}, close: () => {} });
export const useAuthModal = () => useContext(AuthModalCtx);

export const AuthModalProvider = ({ children }) => {
  const [state, setState] = useState({ open: false, mode: "login" });
  const open = (mode = "login") => setState({ open: true, mode });
  const close = () => setState({ open: false, mode: "login" });
  const location = useLocation();
  const navigate = useNavigate();

  // Open modal if ?auth=login or ?auth=register is present
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const a = params.get("auth");
    if (a === "login" || a === "register") {
      setState({ open: true, mode: a });
      params.delete("auth");
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    } else if (a === "google-error" || a === "pro-pending" || a === "google-ok") {
      if (a === "google-error") {
        const reason = params.get("reason") || "";
        let msg = "Google sign-in failed. Please try again or use email login.";
        if (reason === "denied") msg = "Google sign-in was cancelled. Please try again or use email login.";
        else if (reason === "state_mismatch" || reason === "state_expired") msg = "Sign-in session expired — please try again.";
        else if (reason.startsWith("token_exchange")) msg = `Google rejected the sign-in (${reason.replace("token_exchange_", "") || "config error"}). If this keeps happening, the site's Google OAuth settings may need updating.`;
        else if (reason === "no_email") msg = "Your Google account didn't share an email address. Please use email login.";
        toast.error(msg);
      }
      else if (a === "pro-pending") toast.info("Your professional application is pending admin verification. You'll get an email once approved.");
      else toast.success("Signed in with Google");
      params.delete("auth");
      params.delete("reason");
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    }
  }, [location.search]);

  return (
    <AuthModalCtx.Provider value={{ open, close }}>
      {children}
      <AuthModal state={state} setState={setState} close={close} />
    </AuthModalCtx.Provider>
  );
};

const AuthModal = ({ state, setState, close }) => {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const { open: openProSignup } = useProSignup();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [role, setRole] = useState("member");

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const continueWithGoogle = () => {
    window.location.href = window.location.origin + "/api/auth/google/login";
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let loggedInUser = null;
      if (state.mode === "login") {
        loggedInUser = await login(form.email, form.password);
        toast.success(`Welcome back, ${loggedInUser.name.split(" ")[0]}`);
      } else {
        loggedInUser = await register(form.name, form.email, form.password);
        toast.success(`Account created · welcome ${loggedInUser.name.split(" ")[0]}`);
      }
      close();
      setForm({ name: "", email: "", password: "" });
      // Role-based redirect after auth (admin/founder → /admin, professional → /professional/dashboard)
      if (loggedInUser) {
        const r = loggedInUser.role;
        if (r === "admin" || r === "founder") navigate("/admin");
        else if (r === "professional") navigate("/professional/dashboard");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={state.open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="" data-testid="auth-modal">
        <DialogTitle className="sr-only">{state.mode === "login" ? "Sign in" : (state.mode === "forgot" ? "Forgot password" : "Create account")}</DialogTitle>
        <DialogDescription className="sr-only">Authenticate to access your MindDhara account.</DialogDescription>
        {state.mode === "forgot" ? (
          <ForgotPasswordPanel onBack={() => setState({ open: true, mode: "login" })} />
        ) : (
        <div className="p-7">
          <div className="md-label-eyebrow mb-2">{state.mode === "login" ? "Welcome back" : "Get started"}</div>
          <h2 className="font-serif text-3xl mb-1">{state.mode === "login" ? "Sign in to MindDhara." : "Create your account."}</h2>
          <p className="text-sm" style={{ color: "var(--md-text-soft)" }}>{state.mode === "login" ? "Continue your journey." : "Track bookings, sessions and referrals."}</p>

          {state.mode === "register" && (
            <div className="mt-5">
              <label className="md-label-eyebrow block mb-1.5">I&apos;m joining as</label>
              <div className="auth-role-tabs" data-testid="auth-role-tabs">
                <button type="button" onClick={() => setRole("member")} className={`auth-role-tab ${role === "member" ? "active" : ""}`} data-testid="auth-role-member">A user looking for support</button>
                <button type="button" onClick={() => setRole("professional")} className={`auth-role-tab ${role === "professional" ? "active" : ""}`} data-testid="auth-role-professional">A professional offering services</button>
              </div>
              {role === "professional" && (
                <p className="text-xs mt-2" style={{ color: "var(--md-text-soft)" }} data-testid="auth-role-professional-hint">
                  Professionals join via a short application. We&apos;ll open the form next, no member account is created.
                </p>
              )}
            </div>
          )}

          {state.mode === "register" && role === "professional" ? (
            <div className="mt-5 space-y-3" data-testid="auth-pro-cta-block">
              <button
                type="button"
                onClick={() => { close(); openProSignup(); }}
                className="btn-primary w-full justify-center"
                data-testid="auth-pro-continue"
              >
                Continue to application
              </button>
              <p className="text-xs text-center" style={{ color: "var(--md-text-muted)" }}>
                We&apos;ll open the professional application form next. Approval takes 3–5 business days.
              </p>
            </div>
          ) : (
          <>
          <button type="button" onClick={continueWithGoogle} className="auth-google-btn mt-5" data-testid="auth-google-btn">
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.48 12c0-.73.13-1.44.36-2.1V7.07H2.18A11 11 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.83z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            Continue with Google
          </button>
          <div className="auth-sep my-4"><span>or with email</span></div>

          <form onSubmit={submit} className="space-y-3">
            {state.mode === "register" && (
              <div>
                <label className="md-label-eyebrow block mb-1.5">Name</label>
                <input required className="md-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="auth-modal-name" />
              </div>
            )}
            <div>
              <label className="md-label-eyebrow block mb-1.5">Email</label>
              <input required type="email" className="md-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="auth-modal-email" />
            </div>
            <div>
              <label className="md-label-eyebrow block mb-1.5">Password</label>
              <input required type="password" minLength={6} className="md-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="auth-modal-password" />
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full justify-center mt-2" data-testid="auth-modal-submit">
              {loading ? "Please wait…" : (state.mode === "login" ? "Sign In" : "Create Account")}
            </button>
          </form>
          </>
          )}

          <p className="text-sm text-center mt-5" style={{ color: "var(--md-text-soft)" }}>
            {state.mode === "login" ? "New here? " : "Have an account? "}
            <button onClick={() => setState({ open: true, mode: state.mode === "login" ? "register" : "login" })} className="font-medium underline" style={{ color: "var(--md-primary)" }} data-testid="auth-modal-toggle">
              {state.mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </p>
          {state.mode === "login" && (
            <>
              <p className="text-sm text-center mt-2" style={{ color: "var(--md-text-soft)" }}>
                <button onClick={() => setState({ open: true, mode: "forgot" })} className="font-medium underline" style={{ color: "var(--md-primary)" }} data-testid="auth-forgot-link">
                  Forgot password?
                </button>
              </p>
            </>
          )}
        </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
